import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Crown, MapPin, Download, Share2, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import api from "../lib/api";
import { imageFor } from "../lib/thrones";

export default function RoyalTitleDetail() {
  const { id } = useParams();
  const [title, setTitle] = React.useState(null);
  const [status, setStatus] = React.useState("loading"); // loading | ok | missing
  const [busy, setBusy] = React.useState("");
  const cardRef = React.useRef(null);

  React.useEffect(() => {
    let active = true;
    setStatus("loading");
    api
      .get(`/royal-titles/${id}`)
      .then((r) => { if (active) { setTitle(r.data); setStatus("ok"); } })
      .catch(() => { if (active) setStatus("missing"); });
    return () => { active = false; };
  }, [id]);

  useSEO(
    title ? `${title.title} — Yorùbá Royal Titles` : "Yorùbá Royal Titles",
    title ? (title.note || `${title.title}, the throne of ${title.town} in ${title.region} Yorùbáland.`) : "",
    title ? title.image : undefined
  );

  const doShare = async (action) => {
    if (!cardRef.current) return;
    setBusy(action);
    await new Promise((res) => setTimeout(res, 80));
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#12100e" });
      if (action === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${title.title}.png`;
        a.click();
      } else {
        const blob = await (await fetch(url)).blob();
        const file = new File([blob], `${title.title}.png`, { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `${title.title}`,
            text: `${title.title} — ${title.town} · via ÀsÀNigHT Worldwide`,
          });
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.download = `${title.title}.png`;
          a.click();
        }
      }
    } catch (e) {
      console.warn("Share card could not be generated or was cancelled:", e);
    } finally {
      setBusy("");
    }
  };

  if (status === "loading") {
    return <div className="bg-night text-cream min-h-[60vh] grid place-items-center" data-testid="royal-detail-loading">Loading…</div>;
  }

  if (status === "missing" || !title) {
    return (
      <div className="bg-night text-cream min-h-[60vh] grid place-items-center px-6 text-center" data-testid="royal-detail-missing">
        <div>
          <h1 className="font-serif text-4xl mb-4">Throne not found</h1>
          <Link to="/royal-titles" className="text-gold hover:underline">← Back to Royal Titles</Link>
        </div>
      </div>
    );
  }

  const heroImg = title.image || imageFor(title.region);

  return (
    <div className="bg-night text-cream" data-testid="royal-detail">
      <header className="relative min-h-[62vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          <img src={heroImg} alt={`${title.title} royal regalia`} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/65 to-night/30" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-14 w-full">
          <nav className="flex items-center gap-2 text-xs text-cream/70 mb-5">
            <Link to="/royal-titles" className="hover:text-gold" data-testid="royal-detail-breadcrumb">Royal Titles</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gold-soft uppercase tracking-widest">{title.region}</span>
          </nav>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 text-gold text-xs font-semibold px-3 py-1 mb-4">
            <MapPin className="w-3.5 h-3.5" /> {title.town}
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] text-gold text-shadow-hero"
            data-testid="royal-detail-title"
          >
            {title.title}
          </motion.h1>
        </div>
      </header>

      {title.note && (
        <section className="max-w-4xl mx-auto px-6 py-16">
          <Reveal>
            <p className="text-xl md:text-2xl font-serif text-cream/90 leading-relaxed border-l-2 border-gold pl-6" data-testid="royal-detail-note">
              {title.note}
            </p>
          </Reveal>
        </section>
      )}

      <section className="max-w-4xl mx-auto px-6 pb-20">
        <Reveal>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => doShare("download")}
              disabled={busy !== ""}
              data-testid="royal-detail-download"
              className="inline-flex items-center gap-2 bg-gold text-night text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-50"
            >
              {busy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download Card
            </button>
            <button
              onClick={() => doShare("share")}
              disabled={busy !== ""}
              data-testid="royal-detail-share"
              className="inline-flex items-center gap-2 border border-cream/25 text-cream text-sm font-semibold px-5 py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
            >
              {busy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Share
            </button>
            <Link
              to="/royal-titles"
              data-testid="royal-detail-back"
              className="inline-flex items-center gap-2 text-cream/60 text-sm font-semibold px-5 py-2.5 rounded-full hover:text-gold transition-colors"
            >
              <Crown className="w-4 h-4" /> Explore more thrones
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Hidden branded share card */}
      <div aria-hidden="true" style={{ position: "fixed", top: 0, left: "-20000px", pointerEvents: "none" }}>
        <div
          ref={cardRef}
          style={{
            width: 1080,
            height: 1080,
            background: "linear-gradient(155deg, #1b3327 0%, #12100e 55%)",
            color: "#F5EFE6",
            padding: 90,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            boxSizing: "border-box",
            position: "relative",
          }}
        >
          <div style={{ position: "absolute", inset: 40, border: "2px solid rgba(212,169,74,0.35)", borderRadius: 28, pointerEvents: "none" }} />
          <div style={{ position: "relative" }}>
            <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 46, color: "#D4A94A", lineHeight: 1 }}>ÀsÀNigHT</div>
            <div style={{ fontSize: 16, letterSpacing: 8, color: "rgba(245,239,230,0.6)", marginTop: 6 }}>WORLDWIDE</div>
            <div style={{ fontSize: 20, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginTop: 40 }}>Yorùbá Royal Titles · Oyè Ọba</div>
          </div>
          <div style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
            <div style={{ fontSize: 22, letterSpacing: 4, textTransform: "uppercase", color: "#E8D5A8", marginBottom: 18 }}>{title.town}</div>
            <div
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                color: "#D4A94A",
                fontSize: title.title.length > 34 ? 62 : title.title.length > 22 ? 80 : 96,
                lineHeight: 1.08,
                marginBottom: 30,
              }}
            >
              {title.title}
            </div>
            {title.note && <div style={{ fontSize: 30, lineHeight: 1.45, color: "#F5EFE6" }}>{title.note}</div>}
          </div>
          <div style={{ position: "relative", borderTop: "1px solid rgba(245,239,230,0.15)", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 26, color: "#D4A94A", letterSpacing: 1 }}>asanightworldwide.com</div>
            {title.region && <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic", fontSize: 24, color: "rgba(245,239,230,0.5)" }}>{title.region}</div>}
          </div>
        </div>
      </div>

      <Newsletter />
    </div>
  );
}
